import type { MobileLayoutItem } from '@iheartjane/data-access';
import { Flex, Skeleton, Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';

import { MOBILE_LAYOUT_LABELS } from './utils';

import styles from './FooterWireframes.module.css';

export const LayoutWireframe = ({
  alignment,
  selected,
}: {
  alignment: 'align_left' | 'align_right';
  selected: boolean;
}) => {
  return (
    <Skeleton>
      <Flex
        flexDirection={alignment === 'align_right' ? 'row-reverse' : 'row'}
        background="grays-ultralight"
        mb={16}
        p={20}
        justifyContent="space-between"
        height={128}
        borderRadius="sm"
      >
        <Flex flexDirection="column" gap={4}>
          <Skeleton.Bone
            width="93px"
            height="35px"
            className={clsx(
              styles['layoutWireframeLogo'],
              styles['unrounded'],
              { [styles['highlighted']]: selected }
            )}
          />
          <Skeleton.Bone width="93px" height="10px" />
          <Skeleton.Bone width="74px" height="10px" />
          <Skeleton.Bone
            width="52px"
            height="16px"
            className={clsx(styles['layoutWireframeTallLine'], {
              [styles['highlighted']]: selected,
            })}
          />
        </Flex>
        <Flex gap={8}>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton.Bone key={`1-${index}`} width="44px" height="10px" />
            ))}
          </Flex>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton.Bone key={`2-${index}`} width="44px" height="10px" />
            ))}
          </Flex>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton.Bone key={`3-${index}`} width="44px" height="10px" />
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Skeleton>
  );
};

export const ImageWireframe = () => {
  return (
    <Skeleton width="312px">
      <Flex
        flexDirection="row"
        background="grays-ultralight"
        mb={16}
        p={20}
        justifyContent="space-between"
        height={128}
        borderRadius="sm"
      >
        <Flex flexDirection="column" gap={4}>
          <Skeleton.Bone
            width="93px"
            height="10px"
            className={styles['highlighted']}
          />
          <Skeleton.Bone
            width="74px"
            height="10px"
            className={styles['highlighted']}
          />
          <Skeleton.Bone
            width="52px"
            height="16px"
            className={styles['highlighted']}
          />
        </Flex>
        <Flex gap={8}>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton.Bone
                key={`image-${index}`}
                width="44px"
                height="10px"
              />
            ))}
          </Flex>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton.Bone
                key={`image-${index}`}
                width="44px"
                height="10px"
              />
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Skeleton>
  );
};

export const CTAWireframe = ({
  alignment,
}: {
  alignment: 'align_left' | 'align_right';
}) => {
  return (
    <Skeleton>
      <Flex
        flexDirection={alignment === 'align_right' ? 'row-reverse' : 'row'}
        background="grays-ultralight"
        mb={16}
        p={20}
        justifyContent="space-between"
        height={128}
        borderRadius="sm"
      >
        <Flex flexDirection="column" gap={4}>
          <Skeleton.Bone
            width="93px"
            height="35px"
            className={styles['unrounded']}
          />
          <Skeleton.Bone
            width="93px"
            height="10px"
            className={styles['highlighted']}
          />
          <Skeleton.Bone
            width="74px"
            height="10px"
            className={styles['highlighted']}
          />
          <Skeleton.Bone
            width="52px"
            height="16px"
            className={styles['highlighted']}
          />
        </Flex>
        <Flex gap={8}>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton.Bone key={`1-${index}`} width="44px" height="10px" />
            ))}
          </Flex>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton.Bone key={`2-${index}`} width="44px" height="10px" />
            ))}
          </Flex>
          <Flex flexDirection="column" gap={4}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton.Bone key={`3-${index}`} width="44px" height="10px" />
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Skeleton>
  );
};

export const MobileLayoutWireframe = ({
  items,
}: {
  items: MobileLayoutItem[];
}) => {
  const renderWireframe = (itemType: string) => {
    switch (itemType) {
      case 'image':
        return (
          <Skeleton>
            <Flex
              flexDirection="column"
              p={8}
              background="grays-ultralight"
              borderRadius="sm"
              border="grays-light"
              width="115px"
            >
              <Typography variant="body-semibold" textAlign="center" mb={8}>
                {MOBILE_LAYOUT_LABELS.image}
              </Typography>
              <Skeleton.Bone
                width="99px"
                height="59px"
                className={styles['mobileImagePlaceholder']}
              />
            </Flex>
          </Skeleton>
        );

      case 'calls_to_action':
        return (
          <Skeleton>
            <Flex
              flexDirection="column"
              p={8}
              background="grays-ultralight"
              borderRadius="sm"
              border="grays-light"
              width="115px"
            >
              <Typography variant="body-semibold" textAlign="center" mb={8}>
                {MOBILE_LAYOUT_LABELS.calls_to_action}
              </Typography>
              <Flex flexDirection="column" gap={12}>
                <Skeleton.Bone width="99px" height="10px" />
                <Skeleton.Bone width="74px" height="10px" />
                <Skeleton.Bone width="52px" height="16px" />
              </Flex>
            </Flex>
          </Skeleton>
        );

      case 'links':
        return (
          <Skeleton>
            <Flex
              flexDirection="column"
              p={8}
              background="grays-ultralight"
              borderRadius="sm"
              border="grays-light"
              width="115px"
            >
              <Typography variant="body-semibold" textAlign="center" mb={8}>
                {MOBILE_LAYOUT_LABELS.links}
              </Typography>
              <Flex flexDirection="column" gap={16}>
                <Flex flexDirection="column" gap={4}>
                  <Skeleton.Bone width="44px" height="10px" />
                  <Skeleton.Bone width="33px" height="10px" />
                  <Skeleton.Bone width="33px" height="10px" />
                </Flex>

                <Flex flexDirection="column" gap={4}>
                  <Skeleton.Bone width="44px" height="10px" />
                  <Skeleton.Bone width="33px" height="10px" />
                  <Skeleton.Bone width="33px" height="10px" />
                </Flex>
              </Flex>
            </Flex>
          </Skeleton>
        );

      case 'social_links':
        return (
          <Skeleton>
            <Flex
              flexDirection="column"
              p={8}
              background="grays-ultralight"
              borderRadius="sm"
              border="grays-light"
              width="115px"
            >
              <Typography variant="body-semibold" textAlign="center" mb={8}>
                {MOBILE_LAYOUT_LABELS.social_links}
              </Typography>
              <Flex
                flexDirection="row"
                gap={4}
                alignItems="center"
                justifyContent="center"
              >
                <Skeleton.Bone
                  width="18px"
                  height="16px"
                  className={styles['mobileSocialIcon']}
                />
                <Skeleton.Bone
                  width="18px"
                  height="16px"
                  className={styles['mobileSocialIcon']}
                />
                <Skeleton.Bone
                  width="18px"
                  height="16px"
                  className={styles['mobileSocialIcon']}
                />
                <Skeleton.Bone
                  width="18px"
                  height="16px"
                  className={styles['mobileSocialIcon']}
                />
              </Flex>
            </Flex>
          </Skeleton>
        );

      case 'text':
        return (
          <Skeleton>
            <Flex
              flexDirection="column"
              p={8}
              background="grays-ultralight"
              borderRadius="sm"
              border="grays-light"
              width="115px"
            >
              <Typography variant="body-semibold" textAlign="center" mb={8}>
                {MOBILE_LAYOUT_LABELS.text}
              </Typography>
              <Flex flexDirection="column" gap={8}>
                <Skeleton.Bone width="99px" height="10px" />
                <Skeleton.Bone width="84px" height="10px" />
                <Skeleton.Bone width="91px" height="10px" />
                <Skeleton.Bone width="84px" height="10px" />
                <Skeleton.Bone width="84px" height="10px" />
                <Skeleton.Bone width="91" height="10px" />
              </Flex>
            </Flex>
          </Skeleton>
        );

      default:
        return null;
    }
  };

  return (
    <Flex flexDirection="column" gap={8} alignItems="center">
      {items.map((item, index) => (
        <div key={index}>{renderWireframe(item)}</div>
      ))}
    </Flex>
  );
};
